import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Autocomplete, MenuItem, TextField } from '@mui/material'
import React from 'react'
import DescriptionValueList from '../../../components/description-value-list'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import {
    aseguradoraLabel,
    CARACTERISTICA_VALOR_MAX,
    COBERTURA_VALOR_MAX,
    DESCRIPCION_MAX,
    RamoMark,
    RAMOS,
    selectMenu,
} from '../common/quote-template-common'
import type { QuoteInsurerOption } from '../common/quote-template-common'
import { useQuoteTemplateFormModel } from './quote-template-form-model'

const QuoteTemplateForm = () => {
    const model = useQuoteTemplateFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <form
                onSubmit={model.save}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'TEXTAREA') event.preventDefault()
                }}
                className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]"
            >
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description={model.isEdit ? 'Actualiza la propuesta preconfigurada.' : 'Guarda una propuesta preconfigurada para llenar cotizaciones en un clic.'}
                    backTo={model.backTo}
                    backLabel="Templates"
                    actions={<span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Datos generales</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <TextField
                            className="v2-field md:col-span-2" variant="filled" fullWidth
                            required
                            label="Nombre del template"
                            value={form.nombre}
                            onChange={(event) => change.nombre(event.target.value)}
                        />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            label="Ramo"
                            value={form.ramo}
                            disabled={model.isEdit}
                            helperText={model.isEdit ? 'El ramo no se puede cambiar' : undefined}
                            onChange={(event) => change.ramo(event.target.value)}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                        >
                            <MenuItem value=""><em>Seleccione</em></MenuItem>
                            {RAMOS.map((ramo) => (
                                <MenuItem key={ramo} value={ramo}>
                                    <span className="flex items-center gap-2"><RamoMark ramo={ramo} />{ramo}</span>
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            label="Moneda"
                            value={form.moneda}
                            onChange={(event) => change.moneda(event.target.value)}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                        >
                            <MenuItem value=""><em>Seleccione</em></MenuItem>
                            {model.monedas.map((moneda) => (
                                <MenuItem key={moneda.id} value={moneda.abreviacion || ''}>{moneda.abreviacion} · {moneda.moneda}</MenuItem>
                            ))}
                        </TextField>
                        <Autocomplete<QuoteInsurerOption>
                            options={model.aseguradoras}
                            value={form.aseguradora}
                            onChange={(_, option) => change.aseguradora(option)}
                            getOptionLabel={(option) => aseguradoraLabel(option)}
                            isOptionEqualToValue={(option, current) => option.id === current.id}
                            noOptionsText="Sin resultados"
                            slotProps={{ paper: { className: 'v2-select-menu' } }}
                            renderInput={(params) => (
                                <TextField {...params} className="v2-field" variant="filled" fullWidth required label="Aseguradora" />
                            )}
                        />
                        <TextField
                            className="v2-field" variant="filled" fullWidth
                            required
                            label="Plan o paquete"
                            value={form.plan}
                            onChange={(event) => change.plan(event.target.value)}
                        />
                    </div>
                </section>

                {!form.ramo && (
                    <div className="rounded-2xl border border-dashed border-k-line-strong bg-k-surface px-5 py-8 text-center text-sm text-k-muted">
                        Elige el ramo para capturar las características y coberturas del plan.
                    </div>
                )}

                {form.ramo && (
                    <section className="k-card">
                        <h2 className="k-card-title">Características del plan</h2>
                        <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                            {model.planFields.map((field) => (
                                <TextField
                                    key={field.key}
                                    className="v2-field" variant="filled" fullWidth
                                    required={field.required}
                                    label={field.label}
                                    value={form[field.key]}
                                    onChange={(event) => change.field(field.key, event.target.value)}
                                />
                            ))}
                        </div>
                        <div className="mt-5 flex flex-col gap-4">
                            {model.fields.caracteristicas && (
                                <DescriptionValueList
                                    title="Características adicionales"
                                    hint="Datos libres del plan: nivel hospitalario, tabulador, red médica…"
                                    rows={form.caracteristicas}
                                    descripcionLabel="Característica"
                                    valorLabel="Valor"
                                    descripcionMax={DESCRIPCION_MAX}
                                    valorMax={CARACTERISTICA_VALOR_MAX}
                                    addLabel="Agregar característica"
                                    emptyText="Sin características adicionales."
                                    onAdd={model.caracteristicas.add}
                                    onChange={model.caracteristicas.change}
                                    onRemove={model.caracteristicas.remove}
                                    onMove={model.caracteristicas.move}
                                />
                            )}
                            <DescriptionValueList
                                title="Coberturas"
                                rows={form.coberturas}
                                descripcionLabel="Cobertura"
                                valorLabel="Valor"
                                descripcionMax={DESCRIPCION_MAX}
                                valorMax={COBERTURA_VALOR_MAX}
                                suggestions={model.coberturaSuggestions}
                                addLabel="Agregar cobertura"
                                emptyText="Sin coberturas."
                                onAdd={model.coberturas.add}
                                onChange={model.coberturas.change}
                                onRemove={model.coberturas.remove}
                                onMove={model.coberturas.move}
                            />
                        </div>
                    </section>
                )}

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.loading || !form.ramo}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar template
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default QuoteTemplateForm
